import { Box, Button, Flex, HStack, Tag, Text, useToast, VStack } from "@chakra-ui/react";
import A from "components/A";
import { formatDate, modifiedUrl, relativeUrl, showErrorToast } from "../../../../utils";
import AnswerActivityOneEvent from "./AnswerActivityOneEvent";
import { useNavigate, useSearchParams } from "react-router-dom";
import AvatarBadge from "./AvatarBadge";

import { useEffect, useState } from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { camelToTitleCase, prettyPrintCodeName } from "utils/text-utils";
import issbAPI from "api/issb/issb";
import { STATUSFIELD_VALUE_LABEL } from "./AdditionalStatuses";
import { useQuery } from "react-query";
import { issbKeys } from "hooks/query";
import LocalSpinner from "../../../../components/LocalSpinner";

// extendedPanel: If true, then the user can click to get more info.

/**
 * Format: {[activityType]: {name: string, extendedPanel: boolean}}
 */
export const ACTIVITY_TYPES = Object.freeze({
  /**
   * For Labeling the activity, please refer to
   * https://esg360.atlassian.net/wiki/spaces/ESG360/pages/153092098/Activity
   */

  UPDATE_ANSWER: {
    name: "Answer added",
    extendedPanel: true,
  },
  // TOOD change files is a gap in the audit trail and the spec
  CHANGE_OWNER: {
    name: "Owner changed",
    extendedPanel: false,
  },
  ASSIGN_ASSIGNEE: {
    name: "Assignee added",
    extendedPanel: false,
  },

  REMOVE_ASSIGNEE: {
    name: "Assignee removed",
    extendedPanel: false,
  },
  CHANGE_STATUS: {
    name: "Status updated",
    extendedPanel: false,
  },
  CHANGE_STAGE: {
    name: "Stage change",
    extendedPanel: false,
  },
  CHANGE_FIELD: {
    name: "Changed",
    extendedPanel: true,
  },
});

export const CHANGE_STATUS_FIELD_MAPPER = Object.freeze({});

/**
 * Fields to ignore in the activity list -- don't show the user these edits.
 */
const IGNORE_FIELDS = ["sourceSlices"];

/**
 * Figma design:
 *
 * Inbox/Detail view
 * The spec -- but this bit is out of date:
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=121-12580&t=brL6Jl51TtcmjDB0-0
 * The spec ...also out of date:
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1301-37741&t=brL6Jl51TtcmjDB0-0
 * "The Spec" is a bit vague.
 *
 * https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-7786&t=EChulq1F8bSWf6oB-0
 * https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-7052&t=cU0ikWuVCkyYSNrw-0
 * https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-7131&t=cU0ikWuVCkyYSNrw-0
 * Older? design:
 * https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=121-12580&t=leB44EcuC9e1asfF-0
 *
 * @param {?Object} answerData - Answer data Can be null while loading
 *
 */
const AnswerDetailActivity = ({ questionData, applyActivity, canSave }) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [activityId, setActivityId] = useState();

  const {
    data: activities,
    isLoading,
    error,
  } = useQuery([issbKeys.answer(), questionData?.id], () => issbAPI.getActivities({ id: questionData?.id }), {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  if (isLoading) {
    return <LocalSpinner size="sm" />;
  }

  // Focus on one activity?
  if (activityId) {
    let activity = activities?.find((a) => "" + a.id === activityId); // NB use ""+ allow int/string comparison
    if (!activity) {
      console.log("Focus parameter activityId=" + activityId + " - but no match found", activities);
    } else {
      // default to show-extendedPanel if the behaviour is not defined
      if (ACTIVITY_TYPES[activity.type]?.extendedPanel || !ACTIVITY_TYPES[activity.type]) {
        return (
          <AnswerActivityOneEvent
            questionId={questionData?.id}
            activity={activity}
            applyActivity={applyActivity}
            canSave={canSave}
            setActivityId={setActivityId}
            questionData={questionData}
          />
        );
      } else {
        console.warn(
          "Expanding Activity.type: "+activity.type+" not implemented - ignoring activityId parameter",
          activity
        );
		// Note: check the url to avoid a react infinite render loop when activityId came from state not url, seen nov 2025
		const modUrl = modifiedUrl({ activityId: null }, window.location.href);
		if (modUrl !== window.location.href) {
			navigate(relativeUrl(modUrl));
		}
      }
    }
  } // .if (activityId)
  // filter out some activities
  let filteredActivities = activities?.filter((activity) => !IGNORE_FIELDS.includes(activity.field));
  // temp HACK: filter out AI activities apart from add-answer
  filteredActivities = filteredActivities?.filter((activity) => !activity.byAI || activity.type === "UPDATE_ANSWER");

  return (
    <VStack spacing={0} align="stretch">
      {filteredActivities?.map((activity) => (
        <AnswerActivityListItem key={activity.id} activity={activity} setActivityId={setActivityId} />
      ))}
    </VStack>
  );
};

/**
 * activity -> label-for-type (can also consider byAI)
 *
 * Note: After checking a few places in the "AIR" Figma file -- the spec for this widget is inconsistent and out of date.
 * This is a best guess at what Product want.
 */
export function labellerTitleForType(activity) {
  let typeLabel = ACTIVITY_TYPES[activity.type]?.name || prettyPrintCodeName(activity.type);
  if (activity.byAI) {
    if (activity.type === "UPDATE_ANSWER") {
      if (activity.aiSuccess) {
        return "AI Answer " + (activity.draft ? "drafted" : "applied");
      } else {
        return "AI: No answer found";
      }
    } else {
      return "AI " + typeLabel;
    }
  } else if (activity.type === "CHANGE_STATUS") {
    return camelToTitleCase(activity?.field) + " changed";
  }
  return typeLabel;
}

const AnswerActivityListItem = ({ activity, setActivityId }) => {
  return (
    <Box
      borderBottom="solid 1px"
      borderColor="gray.200"
      py={4}
      px={0}
      mx={0}
      onClick={() => setActivityId(activity.id)}
    >
      {/*<A href={modifiedUrl({ activityId: activity.id })}>*/}
      {/* User, Title, Date */}
      <HStack justify="space-between" mb={1}>
        <Flex direction="row" gap={2}>
          <Button
            variant="plain"
            fontSize="body.s"
            fontWeight="bold"
            color="darkTeal"
            leftIcon={<AvatarBadge name={activity.createdBy} ai={activity.byAI} />}
            p={0}
          >
            {labellerTitleForType(activity)}
            {activity.type === "CHANGE_FIELD" && <span>&nbsp;{activity.field}</span>}
          </Button>
        </Flex>
        <Text fontSize="body.xs" color="gray.500">
          {formatDate(activity.date)}
        </Text>
      </HStack>

      {activity?.type === "ASSIGN_ASSIGNEE" && (
        <StatusTag field={activity?.field} value={activity?.newValue} mt={2} ml={8} />
      )}

      {activity?.type === "REMOVE_ASSIGNEE" && (
        <StatusTag beforeFlag={true} field={activity?.field} value={activity?.oldValue} mt={2} ml={8} />
      )}

      {activity?.type === "CHANGE_OWNER" && (
        <Flex align="center" gap={2} mt={2} ml={8}>
          <StatusTag beforeFlag={true} field={activity?.field} value={activity?.oldValue} />
          <ArrowRight size={16} />
          <StatusTag field={activity?.field} value={activity?.newValue} />
        </Flex>
      )}

      {activity?.type === "CHANGE_STAGE" && (
        <Flex align="center" gap={2} mt={2} ml={8}>
          <StatusTag beforeFlag={true} field={activity?.field} value={activity?.oldValue} />
          <ArrowRight size={16} />
          <StatusTag field={activity?.field} value={activity?.newValue} />
        </Flex>
      )}

      {activity?.type === "CHANGE_STATUS" && (
        <Flex align="center" gap={2} mt={2} ml={8}>
          <StatusTag beforeFlag={true} field={activity?.field} value={activity?.oldValue} />
          <ArrowRight size={16} />
          <StatusTag field={activity?.field} value={activity?.newValue} />
        </Flex>
      )}

      {activity?.reason && (
        <Text fontSize="body.s" color="gray.500" ml={8} mt={2}>
          {activity?.reason}
        </Text>
      )}
      {/*</A>*/}
    </Box>
  );
};

export function StatusTag({ field, value, beforeFlag, showField, ...props }) {
  let bg = beforeFlag ? "pink" : "yellow.100";
  return (
    <Tag bg={bg} color="black" px={3} py={1} borderRadius="md" {...props}>
      {showField && <span>{prettyPrintCodeName(field)}:&nbsp;</span>}
      {STATUSFIELD_VALUE_LABEL[field]?.[value] ?? value}
    </Tag>
  );
}

export default AnswerDetailActivity;
